// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import tenantService from "../../services/tenantService";

/**
 * Right-panel content shown in the Map Editor when nothing is selected on the
 * canvas. Edits scenario-level fields (name, code, description) and the set
 * of use cases attached to this scenario.
 *
 * Tenant / Location are shown as read-only breadcrumbs with deep-links to
 * their existing admin pages — editing them lives there, not here, to avoid
 * two sources of truth.
 *
 * Local state holds the working values; the parent reads them via the
 * controlled props (`scenarioPatch`, `desiredAssignments`) and persists on
 * the main "Speichern" button.
 */
const ScenarioPropertiesPanel = ({
  scenario,
  useCases,
  assignments,
  scenarioPatch,
  setScenarioPatch,
  desiredAssignments,
  setDesiredAssignments,
}) => {
  const [tenantName, setTenantName] = useState("");
  const [locationName, setLocationName] = useState("");

  // Resolve tenant + location names for the breadcrumb. The scenario record
  // sometimes carries tenant_name/location_name already; fall back to a
  // direct fetch otherwise.
  useEffect(() => {
    if (!scenario) return;
    setTenantName(scenario.tenant_name || scenario.tenant?.name || "");
    setLocationName(scenario.location_name || scenario.location?.name || "");
    if (!scenario.tenant_name && scenario.tenant_id) {
      tenantService
        .getTenantById(scenario.tenant_id)
        .then((r) => setTenantName(r?.name || r?.data?.name || ""))
        .catch(() => {});
    }
  }, [scenario]);

  // Seed local working values from the loaded scenario the first time we
  // see it, but don't clobber in-progress edits.
  useEffect(() => {
    if (!scenario) return;
    setScenarioPatch((prev) => ({
      name: prev?.name ?? scenario.name ?? "",
      scenario_code: prev?.scenario_code ?? scenario.scenario_code ?? "",
      description: prev?.description ?? scenario.description ?? "",
    }));
  }, [scenario, setScenarioPatch]);

  useEffect(() => {
    if (desiredAssignments == null && assignments) {
      setDesiredAssignments(assignments.map((a) => ({ ...a })));
    }
  }, [assignments, desiredAssignments, setDesiredAssignments]);

  const useCasesById = useMemo(
    () =>
      (useCases || []).reduce((acc, uc) => {
        acc[uc.id] = uc;
        return acc;
      }, {}),
    [useCases],
  );

  const fieldClass =
    "w-full px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded";
  const labelClass = "block text-xs text-gray-600 dark:text-slate-400 mb-1";

  if (!scenario) {
    return (
      <div className="text-sm text-gray-500 dark:text-slate-400">
        Lade Szenario…
      </div>
    );
  }

  const patchField = (key, value) =>
    setScenarioPatch((prev) => ({ ...(prev || {}), [key]: value }));

  const assigned = desiredAssignments || [];
  const assignedIds = new Set(assigned.map((a) => a.use_case_id));
  const addableUseCases = (useCases || []).filter(
    (uc) => !assignedIds.has(uc.id),
  );

  const updateAssignment = (ucId, patch) =>
    setDesiredAssignments((prev) =>
      (prev || []).map((a) =>
        a.use_case_id === ucId ? { ...a, ...patch } : a,
      ),
    );

  const removeAssignment = (ucId) =>
    setDesiredAssignments((prev) =>
      (prev || []).filter((a) => a.use_case_id !== ucId),
    );

  const addAssignment = (ucId) => {
    const id = Number(ucId);
    if (!id || assignedIds.has(id)) return;
    const uc = useCasesById[id];
    setDesiredAssignments((prev) => [
      ...(prev || []),
      {
        use_case_id: id,
        name: uc?.name || "",
        is_active: true,
        priority: 0,
        price_multiplier: 1,
      },
    ]);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
          Szenario
        </h3>
        <div className="mt-1 text-xs text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-1">
          {tenantName ? (
            <a
              href="/admin/tenants"
              className="inline-flex items-center gap-1 hover:underline"
            >
              {tenantName}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span>Kein Mandant</span>
          )}
          <span>›</span>
          {locationName ? (
            <a
              href="/admin/standorte-uebersicht"
              className="inline-flex items-center gap-1 hover:underline"
            >
              {locationName}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span>Kein Standort</span>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          value={scenarioPatch?.name ?? ""}
          onChange={(e) => patchField("name", e.target.value)}
          className={fieldClass}
        />
      </div>

      <div>
        <label className={labelClass}>Code</label>
        <input
          type="text"
          value={scenarioPatch?.scenario_code ?? ""}
          onChange={(e) => patchField("scenario_code", e.target.value)}
          className={fieldClass}
        />
      </div>

      <div>
        <label className={labelClass}>Beschreibung</label>
        <textarea
          rows={3}
          value={scenarioPatch?.description ?? ""}
          onChange={(e) => patchField("description", e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
        <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2">
          Nutzungsarten
        </div>

        {assigned.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
            Keine Nutzungsarten zugewiesen.
          </p>
        )}

        <div className="space-y-2">
          {assigned.map((a) => {
            const meta = useCasesById[a.use_case_id];
            return (
              <div
                key={a.use_case_id}
                className="border border-gray-200 dark:border-slate-700 rounded p-2"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                    {meta?.name || a.name || `#${a.use_case_id}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAssignment(a.use_case_id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    entfernen
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelClass}>Priorität</label>
                    <input
                      type="number"
                      value={a.priority ?? 0}
                      onChange={(e) =>
                        updateAssignment(a.use_case_id, {
                          priority: Number(e.target.value) || 0,
                        })
                      }
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Preis-Multiplikator</label>
                    <input
                      type="number"
                      step="0.01"
                      value={a.price_multiplier ?? 1}
                      onChange={(e) =>
                        updateAssignment(a.use_case_id, {
                          price_multiplier: Number(e.target.value) || 1,
                        })
                      }
                      className={fieldClass}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-2 text-xs text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={a.is_active !== false}
                    onChange={(e) =>
                      updateAssignment(a.use_case_id, {
                        is_active: e.target.checked,
                      })
                    }
                  />
                  aktiv
                </label>
              </div>
            );
          })}
        </div>

        {addableUseCases.length > 0 && (
          <div className="mt-2">
            <select
              value=""
              onChange={(e) => {
                addAssignment(e.target.value);
                e.target.value = "";
              }}
              className={fieldClass}
            >
              <option value="">+ Nutzungsart hinzufügen…</option>
              {addableUseCases.map((uc) => (
                <option key={uc.id} value={uc.id}>
                  {uc.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioPropertiesPanel;
